import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { BookOpen, History, BarChart3, Settings, Globe, Menu, X, Star, LogOut, BrainCircuit, Sparkles, Flame, Layers, MessageSquare } from 'lucide-react';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #141414; /* Dark Mode Background */
  z-index: 2147483647;
  height: 60px;
  box-sizing: border-box;
`;

const HeaderContent = styled.div`
  max-width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  box-sizing: border-box;
  color: #fff;

  @media (max-width: 1200px) {
    padding: 0 12px;
  }
`;

const Logo = styled(Link)`
  font-size: 20px;
  font-weight: bold;
  color: #e50914 !important; /* Brand Red - Force Override */
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover, &:visited, &:active {
    color: #e50914 !important;
    text-decoration: none !important;
  }
`;

const MobileMenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  min-width: 40px;
  padding: 0;
  margin: 0;
  margin-left: auto;
  margin-right: 8px;
  
  border: 1px solid #333;
  border-radius: 8px;
  background-color: #141414;
  color: #fff;
  
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 1002;
  position: relative;

  &:hover {
    background-color: #333;
    color: #e50914;
    border-color: #e50914;
  }

  & > svg {
    display: block;
    width: 24px;
    height: 24px;
  }

  @media (min-width: 1200px) {
    display: none;
  }
`;

const Nav = styled.nav`
  display: flex;
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: #141414;
  padding: 16px;
  flex-direction: column;
  gap: 8px;
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-100%)'};
  transition: transform 0.3s ease-in-out, visibility 0.3s;
  z-index: 999;
  overflow-y: auto;
  border-top: 1px solid #333;

  @media (min-width: 1200px) {
    display: flex;
    visibility: visible !important;
    position: static;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    box-shadow: none;
    padding: 0;
    transform: none;
    border-top: none;
    overflow-y: visible;
    background: transparent;
  }
`;

const NavLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  text-decoration: none;
  color: ${props => props.$active ? '#fff' : '#a3a3a3'};
  background-color: ${props => props.$active ? 'rgba(229, 9, 20, 0.2)' : 'transparent'};
  font-weight: ${props => props.$active ? '600' : '500'};
  transition: all 0.2s ease;
  font-size: 16px;

  &:hover {
    color: #fff;
    background-color: rgba(255, 255, 255, 0.1);
  }

  @media (min-width: 1200px) {
    font-size: 14px;
    padding: 0 16px;
    height: 36px;
    line-height: normal;
    justify-content: center;
    background-color: transparent;
    color: ${props => props.$active ? '#e50914' : '#a3a3a3'};
    
    &:hover {
        color: #fff;
        background-color: transparent;
    }
  }
`;

const LanguageSelector = styled.select`
  padding: 8px 12px;
  border: 1px solid #333;
  border-radius: 8px;
  background: #1f1f1f;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  margin-top: 8px;

  @media (min-width: 1200px) {
    margin-top: 0;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  z-index: 998;
  display: ${props => props.$isOpen ? 'block' : 'none'};

  @media (min-width: 1200px) {
    display: none;
  }
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  background: transparent;
  width: 100%;
  text-align: left;
  cursor: pointer;
  
  text-decoration: none;
  color: #a3a3a3;
  font-weight: 500;
  transition: all 0.2s ease;
  font-size: 16px;
  font-family: inherit;

  &:hover {
    color: #ef4444;
    background-color: rgba(239, 68, 68, 0.1);
  }

  @media (min-width: 1200px) {
    font-size: 14px;
    padding: 0 16px;
    width: auto;
    height: 36px;
    line-height: normal;
    justify-content: center;
  }
`;

const StreakBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(255, 107, 0, 0.15); /* Orange tint */
  color: #ff6b00; /* Orange Flame Color */
  font-weight: 700;
  font-size: 14px;
  margin-right: 8px;
  
  @media (max-width: 1200px) {
    margin-right: 0;
    margin-bottom: 8px;
    width: fit-content;
  }
`;

function Header({ onLogout, user }) {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navItems = [
    { path: '/feed', label: 'Feed', icon: Layers }, // Feed added
    { path: '/mystory', label: 'MyStory', icon: BookOpen }, // Added
    { path: '/ai-study', label: 'AI 학습', icon: Sparkles },
    { path: '/sentence', label: '문장 분석', icon: BookOpen },
    { path: '/sentence-history', label: '문장 히스토리', icon: History },
    { path: '/word-history', label: '단어 히스토리', icon: History },
    { path: '/quiz', label: '퀴즈', icon: BrainCircuit },
    { path: '/quiz?mode=review', label: '오답노트', icon: BrainCircuit },
    { path: '/word-history?filter=favorites', label: '즐겨찾기', icon: Star },
    { path: '/dashboard', label: '대시보드', icon: BarChart3 },
    // { path: '/data-generator', label: '가격데이터생성', icon: Settings }, // Hidden for production (Startup internal use)
    { path: '/community', label: '소통게시판', icon: MessageSquare }, // Added
  ];

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo to="/" onClick={closeMobileMenu}>
          <BookOpen size={24} />
          누름보카
        </Logo>

        <MobileMenuButton onClick={toggleMobileMenu} aria-label={isMobileMenuOpen ? "닫기" : "메뉴"}>
          {isMobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </MobileMenuButton>

        <Overlay $isOpen={isMobileMenuOpen} onClick={closeMobileMenu} />

        <Nav $isOpen={isMobileMenuOpen}>
          {navItems.map(({ path, label, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              $active={location.pathname + location.search === path || (path === '/word-history' && location.pathname === '/word-history' && !location.search.includes('filter=favorites'))}
              onClick={closeMobileMenu}
            >
              <Icon size={20} />
              {label}
            </NavLink>
          ))}



          <NavButton onClick={() => { closeMobileMenu(); if (onLogout) onLogout(); }}>
            <LogOut size={20} />
            로그아웃
          </NavButton>

        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
}

export default Header;
